import { Link } from "react-router-dom";
import {
  ArrowRight,
  Clock,
  HeartHandshake,
  Lock,
  Phone,
  Smartphone,
  TrendingUp,
  Users,
  Sparkles,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const benefits = [
  {
    icon: Clock,
    title: "Fast decisions",
    desc: "No waiting weeks. We review applications within 24 hours.",
    detail:
      "Our team processes every application the same business day it's received. You'll get a clear yes or no — not a runaround.",
  },
  {
    icon: TrendingUp,
    title: "No hidden charges",
    desc: "Your interest and total cost are shown before you commit.",
    detail:
      "We show you the exact monthly payment, total interest, and total repayment before you submit. What you see is what you pay.",
  },
  {
    icon: Smartphone,
    title: "Mobile money payouts",
    desc: "Receive funds via Airtel, MTN, or Zamtel. Your choice.",
    detail:
      "No need to visit a bank branch. Choose your preferred mobile money provider and get funded directly to your phone.",
  },
  {
    icon: HeartHandshake,
    title: "Flexible repayment",
    desc: "Choose 6 to 24 months. Pay early with no penalties.",
    detail:
      "Pick a repayment schedule that fits your budget. If you come into money early, settle your loan without any extra fees.",
  },
  {
    icon: Lock,
    title: "Your data is secure",
    desc: "Bank-level encryption protects your information.",
    detail:
      "All data is encrypted in transit and at rest. We never share your personal information with third parties without your consent.",
  },
  {
    icon: Phone,
    title: "Real support",
    desc: "Talk to a real person when you need help.",
    detail:
      "Our support team is based in Zambia. Reach us by email or phone — no chatbots, no automated menus.",
  },
];

const differentiators = [
  {
    icon: Users,
    title: "Built for Zambia",
    desc: "We understand the local market. Our products, currency, and payout methods are designed specifically for Zambian borrowers.",
  },
  {
    icon: Sparkles,
    title: "Simple by design",
    desc: "No jargon, no complex forms. We stripped away everything unnecessary so you can focus on what matters — getting funded.",
  },
  {
    icon: ShieldCheck,
    title: "Compliance first",
    desc: "KYC verification ensures every borrower is protected. We follow best practices to keep the platform safe for everyone.",
  },
];

export default function WhyUsPage() {
  return (
    <div className="container mx-auto px-6 pb-20">
      {/* Hero */}
      <section className="mx-auto max-w-3xl pt-16 pb-12 text-center lg:pt-20">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">
          Why Ngwees
        </p>
        <h1 className="text-3xl font-bold text-foreground font-display sm:text-4xl lg:text-5xl">
          Built around what borrowers{" "}
          <span className="bg-gradient-to-r from-primary to-emerald-600 bg-clip-text text-transparent">
            actually care about
          </span>
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground leading-relaxed">
          We're not trying to be everything. We focus on doing one thing well — making borrowing simple, transparent, and fast.
        </p>
      </section>

      {/* Benefits */}
      <section className="mx-auto max-w-4xl">
        <div className="grid gap-5 sm:grid-cols-2">
          {benefits.map((b) => (
            <div
              key={b.title}
              className="rounded-2xl border border-border/40 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <b.icon className="h-5 w-5" />
                </div>
                <h3 className="text-base font-semibold text-foreground">
                  {b.title}
                </h3>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {b.detail}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Differentiators */}
      <section className="mx-auto max-w-4xl pt-20">
        <h2 className="mb-8 text-center text-2xl font-bold text-foreground font-display">
          What sets us apart
        </h2>
        <div className="grid gap-6 md:grid-cols-3">
          {differentiators.map((d) => (
            <div
              key={d.title}
              className="rounded-2xl border border-border/50 bg-white p-7 text-center shadow-sm"
            >
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                <d.icon className="h-6 w-6" />
              </div>
              <h3 className="text-base font-semibold text-foreground">
                {d.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {d.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-3xl pt-16 text-center">
        <div className="rounded-3xl bg-foreground px-8 py-12 text-background shadow-2xl">
          <h2 className="text-2xl font-bold font-display sm:text-3xl">
            See for yourself
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-background/70">
            Join borrowers across Zambia who chose a simpler way to get funded.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="h-12 rounded-xl bg-white px-8 text-foreground shadow-lg hover:bg-white/90"
            >
              <Link to="/register">
                Get started free
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="h-12 rounded-xl border-white/25 px-8 text-white hover:bg-white/10 hover:text-white"
            >
              <Link to="/how-it-works">See how it works</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
